import React, { useEffect, useRef, useMemo, useState } from "react";
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT, HEADER_COL_WIDTH, HEADER_ROW_HEIGHT } from "../../constants";
import { renderSimpleChart } from "../../charts/chartRenderer";
import { computePane, PANE_Z } from "./paneClip";
import { startObjectDrag, RESIZE_HANDLE_STYLE, SELECTION_FRAME_STYLE } from "./objectDrag";

/**
 * ExcelChart renders ONE pane-copy of a chart.  VisualOverlay mounts up
 * to four copies (body / frozen-row / frozen-col / corner); each copy is
 * shifted to stay pinned in its frozen axis and clipped to its pane, so a
 * chart that straddles a freeze line shows its frozen portion pinned while
 * the rest scrolls — exactly like Excel.  A copy renders nothing when the
 * chart has no portion inside its pane.
 */
function ExcelChart({
  chart, colWidths, rowHeights, zoomScale, hiddenCols, hiddenRows,
  pane = "body",
  freezeX = HEADER_COL_WIDTH, freezeY = HEADER_ROW_HEIGHT,
  scrollX = 0, scrollY = 0,
  movable = false, // drag-to-move / resize only while the reader is editing
  adjust = null,    // { dx, dy, w, h } user move/resize (content px), owned by VisualOverlay
  objKey,           // stable id of this object within the sheet
  onAdjust,         // (objKey, geom) => void
  selected = false, // selected in edit mode (copy target)
  onSelect,         // (objKey, geom {left, top, width, height}) => void
}) {
  const canvasRef = useRef(null);

  // User move/resize deltas in CONTENT px (view-side only). They come from
  // VisualOverlay so every freeze-pane copy of this chart moves together.
  // Feeding them into `position`/`size` below keeps the pane clip math AND
  // VisualOverlay's imperative scroll repositioning (data-cl/data-ct)
  // consistent with the moved chart.
  const userDelta = adjust && (adjust.dx || adjust.dy) ? { dx: adjust.dx || 0, dy: adjust.dy || 0 } : null;
  const userSize = adjust && adjust.w ? { w: adjust.w, h: adjust.h } : null;
  const [interacting, setInteracting] = useState(false);
  const rootRef = useRef(null);

  // Compute position and size synchronously to avoid a flash at (0,0)
  const { position, size } = useMemo(() => {
    if (!chart.range) return { position: { top: 0, left: 0 }, size: { width: 400, height: 300 } };

    const range = chart.range;
    const headerRowHeight = HEADER_ROW_HEIGHT;
    const headerColWidth = HEADER_COL_WIDTH;
    const isSparkline = !!chart.isSparkline;

    // Hidden rows/cols are NOT rendered by the grid, so they must not be
    // counted here either — otherwise every chart below/right of a hidden
    // row/col is offset by the hidden sizes.
    const isColHidden = (i) => !!(hiddenCols && hiddenCols.has && hiddenCols.has(i));
    const isRowHidden = (i) => !!(hiddenRows && hiddenRows.has && hiddenRows.has(i));

    // Calculate top-left position (sum of visible row/col sizes up to tl)
    let tlLeft = 0, tlTop = 0;
    for (let i = 0; i < (range.tl?.col || 0); i++) {
      if (!isColHidden(i)) tlLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
    }
    for (let i = 0; i < (range.tl?.row || 0); i++) {
      if (!isRowHidden(i)) tlTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
    }
    // Add EMU sub-cell offsets
    if (range.tl?.colOff) tlLeft += range.tl.colOff / 914400 * 96;
    if (range.tl?.rowOff) tlTop += range.tl.rowOff / 914400 * 96;

    // Add header offsets
    tlLeft += headerColWidth;
    tlTop += headerRowHeight;

    // Calculate bottom-right position. For two-cell anchors we have an
    // explicit "to" cell; for one-cell / absolute anchors the size is
    // expressed as an EMU extent (range.ext = { cx, cy }) instead.
    let brLeft = 0, brTop = 0;
    if (range.br) {
      for (let i = 0; i < range.br.col; i++) {
        if (!isColHidden(i)) brLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
      }
      for (let i = 0; i < range.br.row; i++) {
        if (!isRowHidden(i)) brTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
      }
      if (range.br.colOff) brLeft += range.br.colOff / 914400 * 96;
      if (range.br.rowOff) brTop += range.br.rowOff / 914400 * 96;

      brLeft += headerColWidth;
      brTop += headerRowHeight;
    }

    // EMU → pixels: 914400 EMU per inch, 96 px per inch (≈ 1 px = 9525 EMU)
    const EMU_TO_PX = 96 / 914400;

    // Minimum chart dimensions — sparklines must fit inside their host cell,
    // so they get no enforced minimum. Regular charts only get a tiny floor:
    // inflating a two-cell-anchored chart beyond its authored cells makes it
    // cover the wrong part of the sheet ("position is wrong").
    const minW = isSparkline ? 0 : 40;
    const minH = isSparkline ? 0 : 30;

    let width;
    let height;
    if (range.br) {
      width = Math.max(minW, brLeft - tlLeft);
      height = Math.max(minH, brTop - tlTop);
    } else if (range.ext && (range.ext.cx || range.ext.cy)) {
      width = Math.max(minW, Math.round(range.ext.cx * EMU_TO_PX));
      height = Math.max(minH, Math.round(range.ext.cy * EMU_TO_PX));
    } else {
      width = isSparkline ? (colWidths[range.tl?.col || 0] || DEFAULT_COL_WIDTH) : 400;
      height = isSparkline ? (rowHeights[range.tl?.row || 0] || DEFAULT_ROW_HEIGHT) : 300;
    }

    return { position: { top: tlTop, left: tlLeft }, size: { width, height } };
  }, [chart, colWidths, rowHeights, hiddenCols, hiddenRows]);

  // Anchored geometry + the user's move/resize deltas (content px).
  const posLeft = position.left + (userDelta ? userDelta.dx : 0);
  const posTop = position.top + (userDelta ? userDelta.dy : 0);
  const chartW = userSize ? userSize.w : size.width;
  const chartH = userSize ? userSize.h : size.height;

  // Drag-to-move / drag-corner-to-resize (edit mode only). A mousedown
  // also SELECTS the chart, so Ctrl+C can copy it.
  const beginDrag = (e, mode) => {
    if (!movable || e.button !== 0) return;
    if (typeof onSelect === "function") {
      onSelect(objKey, { left: posLeft, top: posTop, width: chartW, height: chartH });
    }
    setInteracting(true);
    startObjectDrag(
      e, mode, rootRef.current,
      { dx: userDelta ? userDelta.dx : 0, dy: userDelta ? userDelta.dy : 0, w: chartW, h: chartH },
      (g) => { if (typeof onAdjust === "function") onAdjust(objKey, g); },
      () => setInteracting(false),
      { minW: 120, minH: 90 },
    );
  };

  // Freeze-pane geometry for THIS pane (content px). null => this copy has
  // no portion inside its pane and should not render.
  const paneGeom = useMemo(
    () => computePane(pane, posLeft, posTop, chartW, chartH, freezeX, freezeY, scrollX, scrollY),
    [pane, posLeft, posTop, chartW, chartH, freezeX, freezeY, scrollX, scrollY]
  );

  // Scale position (incl. pane pin offset) and size by zoomScale.
  const scaledTop = Math.round((posTop + (paneGeom?.offsetY || 0)) * zoomScale);
  const scaledLeft = Math.round((posLeft + (paneGeom?.offsetX || 0)) * zoomScale);
  const scaledWidth = Math.round(chartW * zoomScale);
  const scaledHeight = Math.round(chartH * zoomScale);

  // Render chart on canvas with device pixel ratio scaling for crisp output
  useEffect(() => {
    if (canvasRef.current && !chart.placeholder && scaledWidth > 0 && scaledHeight > 0) {
      const canvas = canvasRef.current;
      const dpr = window.devicePixelRatio || 1;
      // Set physical canvas resolution
      canvas.width = scaledWidth * dpr;
      canvas.height = scaledHeight * dpr;
      // Scale ctx so drawing commands use logical pixels
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      renderSimpleChart(canvas, chart, scaledWidth, scaledHeight);
    }
  }, [scaledWidth, scaledHeight, chart]);

  // This pane has no portion of the chart — render nothing.
  if (!paneGeom) return null;

  const zIndex = PANE_Z[pane] || 10;
  const clipStyle = paneGeom.clipPath
    ? { clipPath: paneGeom.clipPath, WebkitClipPath: paneGeom.clipPath }
    : null;

  // Data attributes let VisualOverlay's imperative scroll handler reposition
  // and re-clip this copy per frame WITHOUT a React re-render (smooth
  // pinning). They carry the user-adjusted geometry so a moved/resized
  // chart stays where the reader put it while scrolling.
  const paneData = {
    "data-pane": pane,
    "data-cl": posLeft,
    "data-ct": posTop,
    "data-cw": chartW,
    "data-ch": chartH,
  };

  if (chart.placeholder) {
    return (
      <div
        className="excel-chart-placeholder"
        {...paneData}
        style={{
          position: 'absolute',
          top: scaledTop,
          left: scaledLeft,
          width: scaledWidth,
          height: scaledHeight,
          border: '2px dashed #ccc',
          backgroundColor: '#f9f9f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex,
          pointerEvents: 'auto',
          ...clipStyle,
        }}
      >
        <div style={{ textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold' }}>📊 Chart</div>
          <div style={{ fontSize: '12px' }}>{chart.title || 'Chart'}</div>
        </div>
      </div>
    );
  }

  const isSparkline = !!chart.isSparkline;

  return (
    <div
      ref={rootRef}
      className={isSparkline ? "excel-sparkline-overlay" : "excel-chart-overlay"}
      {...paneData}
      title={isSparkline || !movable ? undefined : "Drag to move · drag the corner handle to resize"}
      onMouseDown={isSparkline || !movable ? undefined : (e) => beginDrag(e, "move")}
      style={{
        position: 'absolute',
        top: scaledTop,
        left: scaledLeft,
        width: scaledWidth,
        height: scaledHeight,
        zIndex: interacting ? 40 : zIndex,
        pointerEvents: 'auto',
        cursor: isSparkline || !movable ? undefined : 'move',
        userSelect: 'none',
        ...(interacting ? {} : clipStyle),
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: scaledWidth,
          height: scaledHeight,
          border: isSparkline ? 'none' : '1px solid #ddd',
          boxSizing: 'border-box',
          backgroundColor: isSparkline ? 'transparent' : '#fff',
          borderRadius: isSparkline ? 0 : '4px',
          boxShadow: isSparkline ? 'none'
            : (interacting ? '0 2px 10px rgba(0,0,0,0.25)' : '0 1px 4px rgba(0,0,0,0.08)'),
          display: 'block',
        }}
      />
      {!isSparkline && movable && (selected || interacting) && (
        <div style={SELECTION_FRAME_STYLE} />
      )}
      {!isSparkline && movable && selected && (
        <div onMouseDown={(e) => beginDrag(e, "resize")} style={RESIZE_HANDLE_STYLE} />
      )}
    </div>
  );
}

// PERF: memo'd — chart objects and size arrays are stable per sheet, so
// grid re-renders (selection, hover, scroll state) skip the whole chart
// subtree instead of re-running position math for every chart.
export default React.memo(ExcelChart);
